import { Stack } from "@chakra-ui/react";
import { useEffect, useLayoutEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { validate } from "uuid";

import { useAuth } from "@equidefi/portals/hooks/useAuth";
import { useSingleTab } from "@equidefi/portals/hooks/useSingleTab";
import { offeringIsTerminated } from "@equidefi/shared";

import NavBar from "@/components/nav/NavBar";
import { MultipleTabModal } from "@/components/workflow/MultipleTabModal";
import { canRouteToStep } from "@/helpers/routing";
import { useUserInvestments } from "@/hooks/useInvestments";

import NotFound from "@/pages/NotFound";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";
import VaultProvider from "@equidefi/portals/context/VaultProvider";
import LoadingView from "./WorkflowLoadingView";

const LOADING_TIME = 2250;
const ONE_YEAR = 60 * 60 * 24 * 365; // seconds

const buildOfferingUrl =
  (slug) =>
  (...pieces) =>
    `/offerings/${slug}/${pieces.filter(Boolean).join("/")}`;

const WorkflowLayout = ({ children }) => {
  const { isLoading, investment, startUri, position, offeringError, user, offering } = useWorkflowContext();

  const { isLoggedIn } = useAuth();
  const { slug, investment_id: investmentId } = useParams();
  const { data: offeringInvestments } = useUserInvestments(
    { offering_id: offering?.id },
    { enabled: isLoggedIn && !!offering?.id, refetchOnMount: "always" },
  );
  const isValidUuid = validate(investmentId);

  // Only guard against multiple tabs inside an active investment — not the
  // registration/login/mfa/choose screens, which have no investment_id. The
  // lock is scoped to this specific investment so two different investments can
  // still be worked on in separate tabs.
  const isDuplicateTab = useSingleTab({
    enabled: isValidUuid,
    name: `equidefi-investment-${investmentId}`,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const [, setCookie] = useCookies(["bcode", `ref_${slug}`]);
  const [delay, setDelay] = useState(true);

  const canRouteToNextStep = canRouteToStep(startUri, investment);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Purely for visual purposes to simulate loading the offering
      setDelay(false);
    }, LOADING_TIME);

    return () => clearTimeout(timeoutId);
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [position, location.pathname]);

  useEffect(() => {
    let redirectUrl;
    const offeringUrl = buildOfferingUrl(slug);
    // MFA Code
    const code = searchParams.get("code");

    // Broker tracking code
    const bcode = searchParams.get("bcode");

    // Affiliate tracking code
    const ref = searchParams.get("ref");

    // If there is a code, we need to bypass the check and go to the MFA page as expected
    if (code) {
      return () => {};
    }

    if (bcode) {
      setCookie("bcode", bcode, { path: "/", maxAge: ONE_YEAR });
    }

    if (ref && slug) {
      setCookie(`ref_${slug}`, ref, { path: "/", maxAge: ONE_YEAR });
    }

    // The Open API landing page (start/:token) runs its own authentication off
    // the registration token — for a visitor with no session yet AND for the
    // just-verified session mid password step — so the layout must not route
    // it anywhere.
    if (location.pathname.includes("/start/")) {
      return () => {};
    }

    // Bypass routing on the payment-processing page. Stripe lands here with
    // payment_intent and crypto (BVNK) with ?crypto. The processing page
    // refreshes the investment and forwards to the next step itself, so the
    // layout must not redirect using stale workflow state (doing so bounced
    // crypto payers back to the payment step).
    if (searchParams.get("payment_intent") || searchParams.get("crypto")) {
      return () => {};
    }

    // Bypass routing if editing from review page
    if (location.state?.returnTo) {
      return () => {};
    }

    // If there is an investmentId is in the params, make sure investment is loaded before proceeding
    // If there is no investmentId, we can just execute the below code.
    if (investmentId && isLoading) {
      return () => {};
    }

    const search = new URLSearchParams(location.search);
    const withSearch = (url) => {
      const searchString = search.toString();
      return searchString ? `${url}?${searchString}` : url;
    };

    if (!slug) {
      // Unlikely, but will guard against not having a slug available
      redirectUrl = withSearch("/");
    } else if (offeringIsTerminated(offering)) {
      // If offering completed, then redirect to investor vault
      redirectUrl = withSearch(offeringUrl("login"));
    } else if (!isLoggedIn) {
      // If user token is missing or invalid (expired), go to registration/login screen
      if (location.pathname.includes("mfa")) {
        redirectUrl = withSearch(offeringUrl("mfa"));
      } else if (investmentId || location.pathname.includes("login")) {
        redirectUrl = withSearch(offeringUrl("login"));
      } else {
        redirectUrl = withSearch(offeringUrl("registration"));
      }
    } else if (!investmentId) {
      if (offeringInvestments?.length === 0) {
        redirectUrl = withSearch(offeringUrl("registration"));
      } else if (location.pathname.includes("reinvest")) {
        redirectUrl = location.pathname;
      } else if (offeringInvestments?.length > 0) {
        redirectUrl = withSearch(offeringUrl("choose"));
      }
    } else if (investmentId && startUri && canRouteToNextStep) {
      // Everything is loaded for investment, so go to correct workflow step
      redirectUrl = withSearch(offeringUrl(investmentId, startUri));
    }

    if (redirectUrl && redirectUrl !== location.pathname) {
      navigate(redirectUrl, { replace: true });
    }
  }, [offering, slug, investmentId, searchParams, offeringInvestments, canRouteToNextStep]);

  if (offeringError?.response?.status === 404) {
    return <NotFound />;
  }

  if (delay || !offering || (isValidUuid && (!investment || isLoading))) {
    return <LoadingView isError={!!offeringError} />;
  }

  return (
    <Stack direction={{ base: "column", lg: "row" }} as="main" minH="100vh" spacing={0} bgColor="background">
      <VaultProvider investment={investment}>
        <NavBar offering={offering} position={position}>
          <MultipleTabModal isOpen={isDuplicateTab} />
          {children}
        </NavBar>
      </VaultProvider>
    </Stack>
  );
};

export default WorkflowLayout;
